import {
	Box,
	Button,
	Chip,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormGroup,
	TextField,
} from '@mui/material'
import { useFormik } from 'formik'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import * as yup from 'yup'
import { BaseTandemAccountModel } from '../../api/clients/tandem/DTOs'
import { ListControlInput } from '../../modules/FormBuilderInterop/Configuration'
import { FixedHeaderWithHint } from '../../utils/HOC/FixedHeaders'

type CreateTandemAccountDialogProps = {
	existingAccountNames: string[]
	onConfirm: (v: BaseTandemAccountModel) => void
	onCancel: () => void
}

export const CreateTandemAccountDialog = observer(
	(props: CreateTandemAccountDialogProps) => {
		const localStore = useLocalObservable(() => ({
			acceptedPayees: [] as string[],
		}))

		const validationSchema = yup.object({
			name: yup
				.string()
				.required('Name is a required field')
				.test(
					'is-unique',
					'A Tandem account with this name already exists in your organization',
					(value) => {
						return (
							value !== undefined &&
							!props.existingAccountNames
								.map((v) => v.toUpperCase().trim())
								.includes(value.toUpperCase().trim())
						)
					},
				),
			description: yup.string(),
		})

		const formik = useFormik({
			initialValues: {
				name: '',
				description: '',
			},
			validationSchema,
			onSubmit: (values) => {
				props.onConfirm({
					name: values.name,
					description: values.description,
					acceptedPayees: localStore.acceptedPayees,
				})
			},
		})

		return (
			<>
				<DialogTitle>Create Tandem Account</DialogTitle>
				<Box
					component={DialogContent}
					display="flex"
					flexDirection="column"
					gap={2}
				>
					<FixedHeaderWithHint
						label="Name"
						hint={formik.touched.name && formik.errors.name}
						hintColor="error.main"
					>
						<TextField
							fullWidth
							id="name"
							name="name"
							value={formik.values.name}
							error={!!formik.errors.name && formik.touched.name}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={formik.isSubmitting}
							autoComplete="off"
						/>
					</FixedHeaderWithHint>
					<FixedHeaderWithHint
						label="Description"
						hint={formik.values.description && formik.touched.description}
						hintColor="error.main"
					>
						<TextField
							fullWidth
							id="description"
							name="description"
							value={formik.values.description}
							error={!!formik.errors.description && formik.touched.description}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={formik.isSubmitting}
							autoComplete="off"
						/>
					</FixedHeaderWithHint>
					<FixedHeaderWithHint label="Accepted Payees">
						<FormGroup>
							<ListControlInput
								onAddItem={action((value) => {
									if (!value || value === '') return
									localStore.acceptedPayees.push(value)
								})}
							/>
							<Box display="flex" flexWrap="wrap" gap={2} paddingY={1}>
								{localStore.acceptedPayees.map((v) => (
									<Chip
										key={v}
										label={v}
										onDelete={action(() => {
											localStore.acceptedPayees =
												localStore.acceptedPayees.filter((val) => val !== v)
										})}
									/>
								))}
							</Box>
						</FormGroup>
					</FixedHeaderWithHint>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={props.onCancel}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={formik.submitForm}
						disabled={
							!formik.dirty ||
							!formik.isValid ||
							formik.isSubmitting ||
							localStore.acceptedPayees.length === 0
						}
					>
						Create
					</Button>
				</DialogActions>
			</>
		)
	},
)
